import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { Toast } from 'primereact/toast';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Button } from 'primereact/button';
import { Tag } from 'primereact/tag';
import { ProgressBar } from 'primereact/progressbar';
import { Chart } from 'primereact/chart';
import { ProgressSpinner } from 'primereact/progressspinner';

import { useAuth } from '../context/AuthContext';

// Configuración de Candidatos con Logos y Colores oficiales representativos
const CANDIDATOS_CONFIG = {
    'APP': { color: '#0055A5', logo: '/logos/app.png' },      // Azul APP
    'PL': { color: '#C8102E', logo: '/logos/pl.jpg' },       // Lápiz Rojo
    'AP': { color: '#8f8b8b', logo: '/logos/ap.png' },       // Lampa AP
    'RP': { color: '#009FD6', logo: '/logos/rp.svg' },       // Celeste Renovación
    'FP': { color: '#FF6600', logo: '/logos/fp.png' },       // Naranja Fuerza Popular
    'SP': { color: '#E31B83', logo: '/logos/sp.svg' },       // Corazón Somos Perú
    'APRA': { color: '#E4002B', logo: '/logos/apra.svg' },   // Rojo Estrella Apra
    'PP': { color: '#0055A5', logo: '/logos/pp.png' },       // Morado Podemos
    'OTROS': { color: '#838884', logo: '/logos/otros.png' },
    'NULOS': { color: '#44ef52', logo: null },
    'OBSERVADOS': { color: '#F59E0B', logo: null }
};

// Subcomponente aislado para el temporizador: evita re-renderizar el gráfico cada segundo
const ContadorRegresivo = React.memo(({ onExpire, resetKey }) => {
    const [segundos, setSegundos] = useState(300);

    useEffect(() => {
        setSegundos(300);
    }, [resetKey]);

    useEffect(() => {
        const timer = setInterval(() => {
            setSegundos((prev) => {
                if (prev <= 1) {
                    onExpire();
                    return 300;
                }
                return prev - 1;
            });
        }, 1000);

        return () => clearInterval(timer);
    }, [onExpire]);

    const min = Math.floor(segundos / 60);
    const s = segundos % 60;
    const tiempoFormateado = `${String(min).padStart(2, '0')}:${String(s).padStart(2, '0')}`;

    return (
        <Tag
            icon="pi pi-clock"
            severity="info"
            value={`Próxima actualización: ${tiempoFormateado}`}
            className="px-3 py-2 text-sm"
        />
    );
});

export const DashboardElectoral = () => {
    const { axiosInstance } = useAuth();
    const toast = useRef(null);

    const [dataDashboard, setDataDashboard] = useState(null);
    const [loading, setLoading] = useState(false);
    const [lastUpdated, setLastUpdated] = useState(Date.now());
    const [logosCargados, setLogosCargados] = useState({});

    // Precargar las imágenes de los logos en memoria para Chart.js
    useEffect(() => {
        const publicUrl = process.env.PUBLIC_URL || '';
        Object.entries(CANDIDATOS_CONFIG).forEach(([key, conf]) => {
            if (conf.logo) {
                const img = new Image();
                // Asegura la ruta absoluta respetando la carpeta public
                img.src = `${publicUrl}${conf.logo}`;
                img.onload = () => {
                    setLogosCargados((prev) => ({ ...prev, [key]: img }));
                };
            }
        });
    }, []);

    const fetchDatosElectorales = useCallback(async (isManual = false) => {
        if (isManual) setLoading(true);
        try {
            const res = await axiosInstance.get('/locales/dashboard/tiempo-real');
            setDataDashboard(res.data);
            setLastUpdated(Date.now());
            if (isManual) {
                toast.current?.show({
                    severity: 'success',
                    summary: 'Actualizado',
                    detail: 'Datos actualizados al minuto.',
                    life: 2500
                });
            }
        } catch (err) {
            console.error("Error al obtener dashboard:", err);
            toast.current?.show({
                severity: 'error',
                summary: 'Error',
                detail: 'No se pudo refrescar el cómputo general.',
                life: 3000
            });
        } finally {
            if (isManual) setLoading(false);
        }
    }, [axiosInstance]);

    // Carga inicial
    useEffect(() => {
        fetchDatosElectorales(true);
    }, [fetchDatosElectorales]);

    // Procesamiento de datos de los candidatos vinculando su color y clave de logo
    const { chartData, partyKeys } = useMemo(() => {
        if (!dataDashboard?.votosPorCandidato) return { chartData: null, partyKeys: [] };

        const candidatosObj = dataDashboard.votosPorCandidato;
        const candidatosOrdenados = Object.entries(candidatosObj)
            .map(([nombre, votos]) => ({ nombre, votos: Number(votos) || 0 }))
            .sort((a, b) => b.votos - a.votos);

        const top4 = candidatosOrdenados.slice(0, 4);
        const otrosVotos = candidatosOrdenados.slice(4).reduce((acc, c) => acc + c.votos, 0);

        const labels = [];
        const dataValues = [];
        const backgroundColors = [];
        const keys = [];

        top4.forEach((c) => {
            const codigo = c.nombre.split('-')[0].trim();
            const config = CANDIDATOS_CONFIG[codigo] || CANDIDATOS_CONFIG['OTROS'];

            labels.push(`     ${codigo}`); // Espacio inicial reservado para dibujar el logo circular
            dataValues.push(c.votos);
            backgroundColors.push(config.color);
            keys.push(codigo);
        });

        // Otros Partidos
        labels.push('     Otros Partidos');
        dataValues.push(otrosVotos);
        backgroundColors.push(CANDIDATOS_CONFIG['OTROS'].color);
        keys.push('OTROS');

        // Nulos / Inválidos
        labels.push('Nulos / Inválidos');
        dataValues.push(dataDashboard.votosNulosInvalidos || 0);
        backgroundColors.push(CANDIDATOS_CONFIG['NULOS'].color);
        keys.push('NULOS');

        // Votos Observados
        labels.push('Votos Observados');
        dataValues.push(dataDashboard.votosObservados || 0);
        backgroundColors.push(CANDIDATOS_CONFIG['OBSERVADOS'].color);
        keys.push('OBSERVADOS');

        return {
            chartData: {
                labels,
                datasets: [
                    {
                        label: 'Cantidad de Votos',
                        data: dataValues,
                        backgroundColor: backgroundColors,
                        borderRadius: 6
                    }
                ]
            },
            partyKeys: keys
        };
    }, [dataDashboard]);

    // Plugin de Chart.js para dibujar los símbolos circulares al lado del eje Y
    const logoPlugin = useMemo(() => {
        return {
            id: 'customBarLogos',
            afterDraw: (chart) => {
                const { ctx, scales: { y } } = chart;
                if (!y) return;

                const meta = chart.getDatasetMeta(0);
                if (!meta || !meta.data) return;

                meta.data.forEach((barElement, index) => {
                    const partyKey = partyKeys[index];
                    const img = logosCargados[partyKey];

                    // Coordenada Y exacta del centro de la barra actual
                    const yPos = barElement.y;

                    // Posición X: justo en el margen izquierdo del área del gráfico
                    const size = 22; // 22x22 px
                    const xPos = y.left - size - 8; // 8px de separación antes de la etiqueta

                    if (img && img.complete && img.naturalWidth > 0) {
                        ctx.save();
                        // Dibuja el logo circular
                        ctx.beginPath();
                        ctx.arc(xPos + size / 2, yPos, size / 2, 0, Math.PI * 2);
                        ctx.closePath();
                        ctx.clip();
                        ctx.drawImage(img, xPos, yPos - size / 2, size, size);
                        ctx.restore();
                    }
                });
            }
        };
    }, [partyKeys, logosCargados]);

    // Opciones del Bar Chart (Horizontal)
    const barChartOptions = useMemo(() => ({
        indexAxis: 'y',
        maintainAspectRatio: false,
        aspectRatio: 0.8,
        animation: false,
        layout: {
            padding: {
                left: 35 // Espacio suficiente para que el logo de 22px se dibuje sin recortarse
            }
        },
        plugins: {
            legend: { display: false },
            tooltip: {
                callbacks: {
                    label: (context) => ` ${context.dataset.label}: ${context.raw.toLocaleString()} votos`
                }
            }
        },
        scales: {
            x: {
                grid: { color: '#f1f5f9' },
                ticks: { color: '#64748b' }
            },
            y: {
                grid: { display: false },
                ticks: {
                    color: '#1e293b',
                    font: { weight: 'bold', size: 12 },
                    padding: 4
                }
            }
        }
    }), []);

    return (
        <div className="grid p-fluid">
            <Toast ref={toast} />

            {/* Cabecera y Reloj de Actualización en Tiempo Real */}
            <div className="col-12">
                <div className="card surface-card shadow-2 border-round-xl border-1 surface-border p-4 mb-2">
                    <div className="flex flex-column md:flex-row justify-content-between align-items-start md:align-items-center gap-3">
                        <div>
                            <span className="text-secondary text-xs font-semibold uppercase tracking-wider">Cómputo en Vivo</span>
                            <h2 className="m-0 text-900 font-bold text-2xl">Seguimiento Electoral — El Porvenir</h2>
                            <span className="text-500 text-xs">Padrón Electoral Oficial: <b>113,285 electores</b></span>
                        </div>
                        <div className="flex align-items-center gap-2">
                            <ContadorRegresivo
                                resetKey={lastUpdated}
                                onExpire={() => fetchDatosElectorales(false)}
                            />
                            <Button
                                icon="pi pi-refresh"
                                className="p-button-outlined p-button-primary"
                                onClick={() => fetchDatosElectorales(true)}
                                loading={loading}
                            />
                        </div>
                    </div>
                </div>
            </div>

            {/* Fila de Métricas / KPIs */}
            <div className="col-12 md:col-3">
                <div className="surface-0 p-3 border-round-xl border-1 surface-border shadow-1">
                    <div className="flex justify-content-between align-items-center mb-2">
                        <span className="text-500 font-semibold text-xs uppercase">Votaron (Participación)</span>
                        <div className="flex align-items-center justify-content-center bg-blue-50 border-round" style={{ width: '2.5rem', height: '2.5rem' }}>
                            <i className="pi pi-users text-blue-600 text-lg" />
                        </div>
                    </div>

                    <div className="text-900 font-bold text-3xl mt-1">
                        {dataDashboard?.votosEmitidosTotal?.toLocaleString() || 0}
                    </div>
                    <span className="text-blue-600 text-xs font-bold block mt-1">
                        {dataDashboard?.porcentajeParticipacion || 0}% de los 113,285
                    </span>
                    <ProgressBar
                        value={dataDashboard?.porcentajeParticipacion || 0}
                        showValue={false}
                        style={{ height: '6px' }}
                        className="mt-2"
                    />
                </div>
            </div>

            <div className="col-12 md:col-3">
                <div className="surface-0 p-3 border-round-xl border-1 surface-border shadow-1">
                    <div className="flex justify-content-between align-items-center mb-2">
                        <span className="text-500 font-semibold text-xs uppercase">Avance de Mesas</span>
                        <div className="flex align-items-center justify-content-center bg-blue-50 border-round" style={{ width: '2.5rem', height: '2.5rem' }}>
                            <i className="pi pi-chart-pie text-green-600 text-lg" />
                        </div>
                    </div>
                    <div className="text-green-600 font-bold text-3xl mt-1">
                        {dataDashboard?.mesasProcesadas || 0} / {dataDashboard?.totalMesas || 0}
                    </div>
                    <span className="text-green-700 text-xs font-bold block mt-1">
                        {dataDashboard?.porcentajeMesas || 0}% procesado
                    </span>
                    <ProgressBar
                        value={dataDashboard?.porcentajeMesas || 0}
                        showValue={false}
                        style={{ height: '6px' }}
                        className="mt-2"
                        color="#22c55e"
                    />
                </div>
            </div>

            <div className="col-12 md:col-3">
                <div className="surface-0 p-3 border-round-xl border-1 surface-border shadow-1">
                    <div className="flex justify-content-between align-items-center mb-2">
                        <span className="text-500 font-semibold text-xs uppercase">Ausentismo Computado</span>
                        <div className="flex align-items-center justify-content-center bg-blue-50 border-round" style={{ width: '2.5rem', height: '2.5rem' }}>
                            <i className="pi pi-chart-pie text-orange-600 text-lg" />
                        </div>
                    </div>
                    <div className="text-orange-600 font-bold text-3xl mt-1">
                        {dataDashboard?.ausentesTotal?.toLocaleString() || 0}
                    </div>
                    <span className="text-500 text-xs block mt-1">Personas que no asistieron</span>
                </div>
            </div>

            <div className="col-12 md:col-3">
                <div className="surface-0 p-3 border-round-xl border-1 surface-border shadow-1">
                    <div className="flex justify-content-between align-items-center mb-2">
                        <span className="text-500 font-semibold text-xs uppercase">Locales con Pendientes</span>
                        <div className="flex align-items-center justify-content-center bg-blue-50 border-round" style={{ width: '2.5rem', height: '2.5rem' }}>
                            <i className="pi pi-building text-red-600 text-lg" />
                        </div>
                    </div>

                    <div className="text-red-500 font-bold text-3xl mt-1">
                        {dataDashboard?.localesIncompletos?.length || 0}
                    </div>
                    <span className="text-500 text-xs block mt-1">Centros por cerrar mesas</span>
                </div>
            </div>

            {/* Bar Chart: Tendencia de Votos con Símbolos y Colores */}
            <div className="col-12 lg:col-7">
                <div className="surface-0 p-4 border-round-xl border-1 surface-border shadow-1 h-full flex flex-column justify-content-between">
                    <div>
                        <div className="flex justify-content-between align-items-center mb-2">
                            <span className="text-900 font-bold text-lg">Resultados Provisorios</span>
                            <Tag severity="success" value="Top 4 + Otros" />
                        </div>
                        <p className="text-500 text-xs mb-3">Conteo general de las mesas computadas en El Porvenir.</p>
                    </div>

                    <div style={{ minHeight: '370px', position: 'relative' }}>
                        {chartData ? (
                            <Chart
                                type="bar"
                                data={chartData}
                                options={barChartOptions}
                                plugins={[logoPlugin]}
                                style={{ height: '370px' }}
                            />
                        ) : (
                            <div className="text-center py-8 text-500">
                                <ProgressSpinner style={{ width: '40px', height: '40px' }} />
                                <div>Cargando escrutinio...</div>
                            </div>
                        )}
                    </div>
                </div>
            </div>

            {/* Tabla: Detalle de Locales Pendientes */}
            <div className="col-12 lg:col-5">
                <div className="surface-0 p-4 border-round-xl border-1 surface-border shadow-1 h-full">
                    <div className="flex justify-content-between align-items-center mb-2">
                        <span className="text-900 font-bold text-lg">Locales Pendientes de Cierre</span>
                        <Tag severity="danger" value={`${dataDashboard?.localesIncompletos?.length || 0} Locales`} />
                    </div>
                    <p className="text-500 text-xs mb-3">Centros de votación que aún no registran el 100% de sus mesas.</p>

                    <DataTable
                        value={dataDashboard?.localesIncompletos || []}
                        rows={5}
                        paginator
                        className="p-datatable-sm"
                        emptyMessage="¡Felicitaciones! Todos los locales del distrito han procesado sus mesas."
                        responsiveLayout="scroll"
                    >
                        <Column field="localNombre" header="Local de Votación" body={(l) => <b>{l.localNombre}</b>} />
                        <Column
                            header="Avance Mesas"
                            body={(l) => (
                                <span>{l.mesasProcesadas} de {l.mesasTotales}</span>
                            )}
                        />
                        <Column
                            header="Faltantes"
                            body={(l) => (
                                <Tag severity="warning" value={`${l.mesasPendientes} mesas`} />
                            )}
                            style={{ textAlign: 'center', width: '110px' }}
                        />
                        <Column
                            field="electoresHabiles"
                            header="Electores"
                            body={(l) => l.electoresHabiles?.toLocaleString()}
                            style={{ textAlign: 'right', width: '100px' }}
                        />
                    </DataTable>
                </div>
            </div>
        </div>
    );
};